/*
 * Copyright 2026 Sony Corporation
 */
import type { Platform } from '@ips-jit/schema';
import { Box, SvgIcon, Typography, type SvgIconProps } from '@mui/material';

/** Four panes: Windows. */
export const WindowsIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path d="M3 5.5 10.5 4.5v7H3v-6Zm8.5-1.1L21 3v8.5h-9.5V4.4ZM3 12.5h7.5v7L3 18.5v-6Zm8.5 0H21V21l-9.5-1.4v-7.1Z" />
  </SvgIcon>
);

/** A penguin: Linux. */
export const LinuxIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path d="M12 2c-2.2 0-3.6 1.8-3.6 4.2 0 1.3.3 2.1-.6 3.6-1 1.6-2.6 3.6-2.6 6.3 0 .6.1 1.2.3 1.7-.9.5-1.9 1-1.9 1.8 0 1 1.6 1.2 3.3 1.8 1 .4 1.8.6 2.4.1.5.3 1.6.5 2.7.5s2.2-.2 2.7-.5c.6.5 1.4.3 2.4-.1 1.7-.6 3.3-.8 3.3-1.8 0-.8-1-1.3-1.9-1.8.2-.5.3-1.1.3-1.7 0-2.7-1.6-4.7-2.6-6.3-.9-1.5-.6-2.3-.6-3.6C15.6 3.8 14.2 2 12 2Zm-1.3 3.4c.4 0 .7.5.7 1.1s-.3 1.1-.7 1.1-.7-.5-.7-1.1.3-1.1.7-1.1Zm2.6 0c.4 0 .7.5.7 1.1s-.3 1.1-.7 1.1-.7-.5-.7-1.1.3-1.1.7-1.1ZM12 8.3l1.8 1-1.8 1-1.8-1 1.8-1Zm0 3.2c1.9 0 3.2 2.1 3.2 4.6 0 1.8-1.3 3-3.2 3s-3.2-1.2-3.2-3c0-2.5 1.3-4.6 3.2-4.6Z" />
  </SvgIcon>
);

export const PLATFORM_LABEL: Record<Platform, string> = { windows: 'Windows', linux: 'Linux' };

export const PlatformIcon = ({ platform, ...props }: SvgIconProps & { platform: Platform }) =>
  platform === 'windows' ? <WindowsIcon {...props} /> : <LinuxIcon {...props} />;

const BACKGROUND: Record<Platform, string> = {
  windows: 'linear-gradient(135deg, #0a5bd3 0%, #3aa0f3 100%)',
  linux: 'linear-gradient(135deg, #2b2b2b 0%, #5a5a5a 100%)',
};

/** The picture at the top of a runtime card: the platform's mark on its colour. */
export const PlatformThumbnail = ({ platform, height = 110 }: { platform: Platform; height?: number }) => (
  <Box
    role="img"
    aria-label={`${PLATFORM_LABEL[platform]} PC`}
    sx={{
      height,
      background: BACKGROUND[platform],
      color: 'common.white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
    }}
  >
    <PlatformIcon platform={platform} sx={{ fontSize: 48, color: platform === 'linux' ? '#f5c518' : 'inherit' }} />
    <Typography variant="h6" component="span" sx={{ fontWeight: 300 }}>
      {PLATFORM_LABEL[platform]}
    </Typography>
  </Box>
);
